<template>
  <lf-checkbox-dropdown
    ref="checkboxDropdown"
    :options="agentOptions"
    :placeholder="
      $t('ACTIVITY.FILTERS.AI_AGENTS.PLACEHOLDER', selected?.length || 1)
    "
    class="activity-hub-filter-dropdown z-21 h-6-5 min-w-full leading-none"
    dropdown-width-full
    custom-dropdown-class="z-30"
    :is-loading="isAiAgentsRequestLoading"
    enable-search
    name="ai_agent_ids"
    width-full
    no-margin
    sort
    :toggle-from-parent
    :model-value="selected || []"
    @change-list-visibility="handleListVisibilityChange"
    @menu:closed="selected = getArrayQueryValue('ai_agent_ids')"
    @update:model-value="updateModelValue"
  >
    <template #pre-search>
      <div class="flex items-center justify-between px-4 py-2">
        <lf-h4>{{ $t("ACTIVITY.FILTERS.AI_AGENTS.AGENT") }}</lf-h4>
        <button class="text-error" @click="updateModelValue([])">
          {{ $t("COMMON.CLEAR") }}
        </button>
      </div>
    </template>
    <template #footer>
      <div class="border-t p-4">
        <button
          class="apply-filters-button w-full rounded-sm py-2-5 text-center font-semibold text-primary"
          @click.stop="applyFilter"
        >
          {{ $t("COMMON.APPLY") }}
        </button>
      </div>
    </template>
  </lf-checkbox-dropdown>
</template>

<script setup lang="ts">
import { onMounted, ref, useTemplateRef, watch } from "vue";
import { useRoute } from "vue-router";

import { storeToRefs } from "pinia";

import { convertToArray } from "@/helpers/common";
import { useCommunicationLogs } from "@/hooks/communicationLogs";
import { useAiAgentsStore } from "@/stores/ai/agents";

import LfCheckboxDropdown from "@/components/ui/inputs/LfCheckboxDropdown.vue";

const { toggleFromParent = false } = defineProps<{
  toggleFromParent?: boolean;
}>();

const route = useRoute();
const { handleDropdownValueUpdate, handleFiltering, getArrayQueryValue } =
  useCommunicationLogs();
const aiAgentsStore = useAiAgentsStore();
const { getAll } = aiAgentsStore;
const { isAiAgentsRequestLoading } = storeToRefs(aiAgentsStore);

const agentOptions = ref<Record<string, string>>({});
const selected = ref<string[]>(getArrayQueryValue("ai_agent_ids"));
const checkboxDropdown = useTemplateRef("checkboxDropdown");
const hasOptionsFetched = ref(false);

const fetchAgents = async () => {
  const agents = await getAll();
  agentOptions.value = agents.reduce<Record<string, string>>((acc, agent) => {
    acc[agent.id] = agent.name;
    return acc;
  }, {});
};

const applyFilter = async () => {
  await handleFiltering({
    value: selected.value,
    queryKey: "ai_agent_ids",
    isMultiple: true,
  });
  if (checkboxDropdown.value) {
    checkboxDropdown.value.showMenu = false;
  }
};

const updateModelValue = async (value: unknown[]) => {
  const result = await handleDropdownValueUpdate({
    value,
    queryKey: "ai_agent_ids",
    isMultiple: true,
    selected: selected.value,
  });
  selected.value = result;
};

const handleListVisibilityChange = async (isVisible: boolean) => {
  if (isVisible && !hasOptionsFetched.value) {
    await fetchAgents();
    hasOptionsFetched.value = true;
  }
};

watch(
  () => route.query["ai_agent_ids"],
  (updatedValue) => {
    if (!updatedValue) {
      selected.value = [];
      return;
    }
    selected.value = convertToArray(updatedValue) as string[];
  }
);

onMounted(async () => {
  if (toggleFromParent && !hasOptionsFetched.value) {
    await fetchAgents();
    hasOptionsFetched.value = true;
  }
});
</script>

<style scoped>
@reference "@/assets/tailwind.css";

.activity-hub-filter-dropdown :deep() {
  .dropdown-wrapper {
    @apply min-w-100;
  }
}
</style>
